import raw from '../data/prices.json'

/**
 * The providers' list prices, as data.
 *
 * `prices.json` is the one file a price refresh rewrites — by hand today, by a
 * scheduled job later — so nothing about the calculator's code changes when a
 * price does. Everything read from it goes through `parsePriceBook`, which a
 * unit test runs over the shipped file: a refresh that writes a malformed book
 * fails CI instead of reaching visitors.
 */

/** Display order everywhere: columns, totals and the share link. */
export const PROVIDER_IDS = ['aws', 'gcp', 'azure', 'oci'] as const
export type ProviderId = (typeof PROVIDER_IDS)[number]

export const STORAGE_CLASSES = ['standard', 'infrequent', 'archive'] as const
export type StorageClass = (typeof STORAGE_CLASSES)[number]

/** One band of a tiered price: `gb` units at `perGb`, then the next band. `null` is unbounded. */
export interface Tier {
  readonly gb: number | null
  readonly perGb: number
}

/** A machine sold in one fixed size. */
export interface FixedMachine {
  readonly name: string
  readonly vcpu: number
  readonly memoryGb: number
  readonly hourly: number
}

/** A machine family sized to order, billed per vCPU and per GB of memory. */
export interface FlexFamily {
  readonly name: string
  readonly perVcpuHour: number
  readonly perGbHour: number
  /** vCPUs are sold in multiples of this; Oracle's OCPU is two. */
  readonly vcpuStep: number
  readonly maxVcpu: number
  readonly minGbPerVcpu: number
  readonly maxGbPerVcpu: number
}

export interface MachineCatalogue {
  readonly fixed: readonly FixedMachine[]
  readonly flex: readonly FlexFamily[]
}

export interface ProviderPrices {
  readonly name: string
  readonly region: string
  readonly regionName: string
  /** ISO date (`YYYY-MM-DD`) the prices were last checked against the provider. */
  readonly updatedAt: string
  readonly source: string
  readonly compute: {
    readonly machines: MachineCatalogue
    /** Windows Server licence, charged per vCPU on top of the machine. */
    readonly windowsPerVcpuHour: number
  }
  readonly database: {
    readonly product: string
    readonly machines: MachineCatalogue
    readonly storagePerGbMonth: number
    /** What high availability multiplies: a standby is usually billed like the primary. */
    readonly haComputeFactor: number
    readonly haStorageFactor: number
  }
  readonly blockStorage: { readonly product: string; readonly perGbMonth: number }
  readonly objectStorage: Readonly<
    Record<StorageClass, { readonly product: string; readonly tiers: readonly Tier[] }>
  >
  readonly transfer: {
    readonly inboundPerGb: number
    /** Free allowances are a first band at zero. */
    readonly outbound: readonly Tier[]
  }
  readonly loadBalancer: { readonly product: string; readonly hourly: number }
}

export interface PriceBook {
  readonly currency: string
  /** 730 by every provider's convention: 8,760 hours a year over 12 months. */
  readonly hoursPerMonth: number
  readonly providers: Readonly<Record<ProviderId, ProviderPrices>>
}

/** Throws with the path of the first problem, so a bad refresh says what it broke. */
export function parsePriceBook(input: unknown): PriceBook {
  const book = record(input, 'book')
  const providers = record(book.providers, 'providers')
  return {
    currency: currencyCode(book.currency, 'currency'),
    hoursPerMonth: positive(book.hoursPerMonth, 'hoursPerMonth'),
    providers: {
      aws: provider(providers.aws, 'providers.aws'),
      gcp: provider(providers.gcp, 'providers.gcp'),
      azure: provider(providers.azure, 'providers.azure'),
      oci: provider(providers.oci, 'providers.oci'),
    },
  }
}

function provider(input: unknown, path: string): ProviderPrices {
  const value = record(input, path)
  const compute = record(value.compute, `${path}.compute`)
  const database = record(value.database, `${path}.database`)
  const block = record(value.blockStorage, `${path}.blockStorage`)
  const objects = record(value.objectStorage, `${path}.objectStorage`)
  const transfer = record(value.transfer, `${path}.transfer`)
  const balancer = record(value.loadBalancer, `${path}.loadBalancer`)

  const storageClass = (name: StorageClass): ProviderPrices['objectStorage'][StorageClass] => {
    const at = `${path}.objectStorage.${name}`
    const entry = record(objects[name], at)
    return {
      product: text(entry.product, `${at}.product`),
      tiers: tiers(entry.tiers, `${at}.tiers`),
    }
  }

  return {
    name: text(value.name, `${path}.name`),
    region: text(value.region, `${path}.region`),
    regionName: text(value.regionName, `${path}.regionName`),
    updatedAt: isoDate(value.updatedAt, `${path}.updatedAt`),
    source: url(value.source, `${path}.source`),
    compute: {
      machines: catalogue(compute.machines, `${path}.compute.machines`),
      windowsPerVcpuHour: price(compute.windowsPerVcpuHour, `${path}.compute.windowsPerVcpuHour`),
    },
    database: {
      product: text(database.product, `${path}.database.product`),
      machines: catalogue(database.machines, `${path}.database.machines`),
      storagePerGbMonth: price(database.storagePerGbMonth, `${path}.database.storagePerGbMonth`),
      haComputeFactor: factor(database.haComputeFactor, `${path}.database.haComputeFactor`),
      haStorageFactor: factor(database.haStorageFactor, `${path}.database.haStorageFactor`),
    },
    blockStorage: {
      product: text(block.product, `${path}.blockStorage.product`),
      perGbMonth: price(block.perGbMonth, `${path}.blockStorage.perGbMonth`),
    },
    objectStorage: {
      standard: storageClass('standard'),
      infrequent: storageClass('infrequent'),
      archive: storageClass('archive'),
    },
    transfer: {
      inboundPerGb: price(transfer.inboundPerGb, `${path}.transfer.inboundPerGb`),
      outbound: tiers(transfer.outbound, `${path}.transfer.outbound`),
    },
    loadBalancer: {
      product: text(balancer.product, `${path}.loadBalancer.product`),
      hourly: price(balancer.hourly, `${path}.loadBalancer.hourly`),
    },
  }
}

function catalogue(input: unknown, path: string): MachineCatalogue {
  const value = record(input, path)
  const fixed = list(value.fixed, `${path}.fixed`).map((entry, index) => {
    const at = `${path}.fixed[${index}]`
    const machine = record(entry, at)
    return {
      name: text(machine.name, `${at}.name`),
      vcpu: positive(machine.vcpu, `${at}.vcpu`),
      memoryGb: positive(machine.memoryGb, `${at}.memoryGb`),
      hourly: price(machine.hourly, `${at}.hourly`),
    }
  })
  const flex = list(value.flex, `${path}.flex`).map((entry, index) => {
    const at = `${path}.flex[${index}]`
    const family = record(entry, at)
    const minGbPerVcpu = positive(family.minGbPerVcpu, `${at}.minGbPerVcpu`)
    const maxGbPerVcpu = positive(family.maxGbPerVcpu, `${at}.maxGbPerVcpu`)
    if (minGbPerVcpu > maxGbPerVcpu) fail(at, 'minGbPerVcpu is above maxGbPerVcpu')
    return {
      name: text(family.name, `${at}.name`),
      perVcpuHour: price(family.perVcpuHour, `${at}.perVcpuHour`),
      perGbHour: price(family.perGbHour, `${at}.perGbHour`),
      vcpuStep: wholePositive(family.vcpuStep, `${at}.vcpuStep`),
      maxVcpu: wholePositive(family.maxVcpu, `${at}.maxVcpu`),
      minGbPerVcpu,
      maxGbPerVcpu,
    }
  })
  if (fixed.length === 0 && flex.length === 0) fail(path, 'has no machines')
  return { fixed, flex }
}

/** Every band but the last is bounded; the last is not, so any quantity can be priced. */
function tiers(input: unknown, path: string): Tier[] {
  const bands = list(input, path).map((entry, index) => {
    const at = `${path}[${index}]`
    const band = record(entry, at)
    return {
      gb: band.gb === null ? null : positive(band.gb, `${at}.gb`),
      perGb: price(band.perGb, `${at}.perGb`),
    }
  })
  if (bands.length === 0) fail(path, 'has no bands')
  bands.forEach((band, index) => {
    const last = index === bands.length - 1
    if (last !== (band.gb === null)) fail(`${path}[${index}].gb`, 'only the last band is unbounded')
  })
  return bands
}

function fail(path: string, problem: string): never {
  throw new Error(`prices.json: ${path} ${problem}`)
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    fail(path, 'must be an object')
  }
  return value as Record<string, unknown>
}

function list(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) fail(path, 'must be an array')
  return value
}

function text(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.trim() === '') fail(path, 'must be a non-empty string')
  return value
}

function price(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    fail(path, 'must be a number of zero or more')
  }
  return value
}

function positive(value: unknown, path: string): number {
  if (price(value, path) === 0) fail(path, 'must be above zero')
  return value as number
}

function wholePositive(value: unknown, path: string): number {
  if (!Number.isInteger(positive(value, path))) fail(path, 'must be a whole number')
  return value as number
}

/** A multiplier that can only add cost: high availability never makes things cheaper. */
function factor(value: unknown, path: string): number {
  if (positive(value, path) < 1) fail(path, 'must be 1 or more')
  return value as number
}

function currencyCode(value: unknown, path: string): string {
  if (!/^[A-Z]{3}$/.test(text(value, path))) fail(path, 'must be an ISO 4217 code')
  return value as string
}

function isoDate(value: unknown, path: string): string {
  const date = text(value, path)
  // A real calendar date: the regex alone would accept 2026-02-31.
  const parsed = new Date(`${date}T00:00:00Z`)
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== date
  ) {
    fail(path, 'must be a YYYY-MM-DD date')
  }
  return date
}

function url(value: unknown, path: string): string {
  if (!/^https:\/\/\S+$/.test(text(value, path))) fail(path, 'must be an https URL')
  return value as string
}

export const PRICES: PriceBook = parsePriceBook(raw)

/**
 * The date shown as "prices updated on": the oldest of the providers', so the
 * page never claims more freshness than its stalest figure has.
 */
export function pricesUpdatedAt(book: PriceBook): string {
  return PROVIDER_IDS.map((id) => book.providers[id].updatedAt).sort()[0] ?? ''
}
